import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { hours, money, openFile } from './useAssistAccess';
import DateField from '../components/DateField';

type Doc = { id: string; kind: string; filename: string; contentType: string; sizeBytes: number; uploadedAt: string };
type AdminReview = { id: string; rating: number; comment: string | null; hidden: boolean; createdAt: string };
type AdminExpert = {
  id: string; email: string; status: string; appliedAt: string; decisionNote: string | null;
  profile: {
    name: string; headline: string; bio: string; specialisms: string[]; qualifications: string; registration: string;
    dbsNumber: string | null; dbsIssued: string | null; dateOfBirth: string | null; dbsConsent: boolean;
    agreementVersion: string | null; hourlyPence: number;
  };
  dbsVerified: boolean; dbsCheckedOn: string | null; reply: string | null; replyAt: string | null;
  agreementAcceptedAt: string | null; documents: Doc[]; rating: number | null; reviewCount: number;
  reviews: AdminReview[]; openConcerns: number; flags: string[];
};
type Concern = {
  id: string; expertId: string; expertName: string | null; familyEmail: string | null; safeguarding: boolean;
  details: string; status: string; outcome: string | null; createdAt: string;
};
type Payout = { expertId: string; name: string; email: string; minutes: number; owedPence: number; paidInPence: number; commissionPence: number };

const DBS_UPDATE_SERVICE = 'https://secure.crbonline.gov.uk/crsc/check?execution=e1s1';
const today = () => new Date().toISOString().slice(0, 10);
const date = (d: string | null) => (d ? new Date(d).toLocaleDateString() : 'not given');
const failed = (e: unknown) => (e instanceof Error ? e.message : 'Failed.');

function ExpertCard({ x, onChange }: { x: AdminExpert; onChange: () => void }) {
  const [checkedOn, setCheckedOn] = useState(today());
  const [originalSeen, setOriginalSeen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function post(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api(path, { method: 'POST', token: true, body });
      onChange();
      return true;
    } catch (e) {
      setError(failed(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function dbs(clear: boolean) {
    if (!clear && x.status === 'APPROVED' && !window.confirm(`Record that the DBS Update Service shows new information? ${x.profile.name} is paused straight away, every family loses access, is refunded for unused time and is emailed.`)) return;
    if (await post(`/assist/admin/experts/${x.id}/dbs-check`, { checkedOn, clear, originalSeen })) setOriginalSeen(false);
  }

  async function decide(action: 'APPROVE' | 'REJECT' | 'PAUSE' | 'REMOVE') {
    if (action !== 'APPROVE' && !note.trim()) { setError('Write the reason first. The consultant sees it.'); return; }
    if (action === 'PAUSE' && !window.confirm(`Pause ${x.profile.name}? They are taken off the list, every family they work with loses access straight away, is refunded for unused time and is emailed.`)) return;
    if (action === 'REMOVE' && !window.confirm(`Remove ${x.profile.name} from Assist? Every family they work with loses access straight away, is refunded for unused time and is emailed.`)) return;
    if (await post(`/assist/admin/experts/${x.id}/decision`, { action, note: note.trim() })) setNote('');
  }

  function open(d: Doc) {
    setError(null);
    void openFile(`/assist/admin/documents/${d.id}`).catch((e) => setError(failed(e)));
  }

  const p = x.profile;
  return (
    <article className="assist-expert">
      <div className="studio-type-head">
        <div><h3 style={{ margin: 0 }}>{p.name}</h3><p className="muted" style={{ margin: 0 }}>{x.email} · {p.headline}</p></div>
        <span className="pill">{x.status}</span>
      </div>
      {x.flags.length > 0 && <ul className="error assist-flags">{x.flags.map((f) => <li key={f}><strong>{f}</strong></li>)}</ul>}
      <p className="muted" style={{ margin: 0 }}>Applied {date(x.appliedAt)} · Agreement {x.agreementAcceptedAt ? `accepted ${date(x.agreementAcceptedAt)} (version ${p.agreementVersion ?? 'unknown'})` : 'not accepted'}</p>
      {x.decisionNote && <p style={{ margin: 0 }}><strong>Decision note:</strong> {x.decisionNote}</p>}
      {x.reply && <p className="ai-text" style={{ margin: 0 }}><strong>Their reply{x.replyAt ? ` (${date(x.replyAt)})` : ''}:</strong> {x.reply}</p>}

      <details>
        <summary className="studio-data-summary">Profile</summary>
        <p className="ai-text">{p.bio}</p>
        <p className="ai-text"><strong>Qualifications:</strong> {p.qualifications}</p>
        <p><strong>Registration:</strong> {p.registration || 'none given'}</p>
        <p><strong>Rate:</strong> {money(p.hourlyPence)} an hour · <strong>Specialisms:</strong> {p.specialisms.join(', ') || 'none'}</p>
      </details>

      <details>
        <summary className="studio-data-summary">Documents ({x.documents.length})</summary>
        {x.documents.length === 0 ? <p className="muted">None uploaded.</p> : (
          <div className="list">
            {x.documents.map((d) => (
              <div key={d.id} className="list-item">
                <div className="list-item-text"><div className="name">{d.filename}</div><div className="list-item-meta">{d.kind} · {date(d.uploadedAt)}</div></div>
                <div className="toolbar"><button type="button" className="btn secondary" onClick={() => open(d)}>Open</button></div>
              </div>
            ))}
          </div>
        )}
      </details>

      <details open={!x.dbsVerified}>
        <summary className="studio-data-summary">DBS {x.dbsVerified ? `checked ${date(x.dbsCheckedOn)}` : 'not checked'}</summary>
        <p style={{ margin: 0 }}><strong>Certificate number:</strong> {p.dbsNumber ?? 'not given'} · <strong>Issued:</strong> {date(p.dbsIssued)} · <strong>Date of birth:</strong> {date(p.dateOfBirth)}</p>
        <p><strong>Consent to Update Service check:</strong> {p.dbsConsent ? 'yes' : 'no'} · <strong>Verified:</strong> {x.dbsVerified ? `yes, ${date(x.dbsCheckedOn)}` : 'no'}</p>
        <p><a href={DBS_UPDATE_SERVICE} target="_blank" rel="noopener noreferrer">Open the DBS Update Service check</a></p>
        <div className="assist-form">
          <div className="field"><label htmlFor={`dbs-on-${x.id}`}>Date checked</label><DateField id={`dbs-on-${x.id}`} max={today()} value={checkedOn} onChange={(v) => setCheckedOn(v ?? '')} /></div>
          <label className="studio-photo-check"><input type="checkbox" checked={originalSeen} onChange={(e) => setOriginalSeen(e.target.checked)} /><span>I have seen the original certificate</span></label>
          <div className="toolbar">
            <button type="button" className="btn" disabled={busy || !checkedOn || !originalSeen} onClick={() => void dbs(true)}>Clear</button>
            <button type="button" className="btn quiet-danger" disabled={busy || !checkedOn} onClick={() => void dbs(false)}>Shows new information</button>
          </div>
        </div>
      </details>

      <details>
        <summary className="studio-data-summary">Reviews: {x.rating != null ? `★ ${x.rating} from ${x.reviewCount}` : `${x.reviewCount} shown`}{x.reviews.length !== x.reviewCount ? `, ${x.reviews.length - x.reviewCount} hidden` : ''}</summary>
        {x.reviews.length === 0 ? <p className="muted">No reviews yet.</p> : (
          <div className="list">
            {x.reviews.map((r) => (
              <div key={r.id} className="list-item">
                <div className="list-item-text">
                  <div className="name">{r.rating} out of 5{r.hidden ? ' · hidden' : ''}</div>
                  {r.comment && <p className="ai-text" style={{ margin: 0 }}>{r.comment}</p>}
                  <div className="list-item-meta">{date(r.createdAt)}</div>
                </div>
                <div className="toolbar">
                  <button type="button" className="btn secondary" disabled={busy} onClick={() => void post(`/assist/admin/reviews/${r.id}/hidden?hidden=${!r.hidden}`)}>{r.hidden ? 'Show' : 'Hide'}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </details>

      <div className="assist-form">
        <div className="field">
          <label htmlFor={`note-${x.id}`}>Reason, shown to the consultant</label>
          <textarea id={`note-${x.id}`} rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="field-hint">Required to reject, pause or remove. Optional when approving.</p>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="toolbar">
          {x.status !== 'APPROVED' && <button type="button" className="btn" disabled={busy} onClick={() => void decide('APPROVE')}>Approve</button>}
          {x.status === 'APPLIED' && <button type="button" className="btn secondary" disabled={busy} onClick={() => void decide('REJECT')}>Reject</button>}
          {x.status === 'APPROVED' && <button type="button" className="btn quiet-danger" disabled={busy} onClick={() => void decide('PAUSE')}>Pause</button>}
          {x.status !== 'REMOVED' && <button type="button" className="btn quiet-danger" disabled={busy} onClick={() => void decide('REMOVE')}>Remove</button>}
        </div>
      </div>
    </article>
  );
}

function ConcernItem({ c, onChange }: { c: Concern; onChange: () => void }) {
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function close() {
    setBusy(true);
    setError(null);
    try {
      await api(`/assist/admin/concerns/${c.id}/close`, { method: 'POST', token: true, body: { outcome } });
      onChange();
    } catch (e) {
      setError(failed(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="assist-work">
      <div className="studio-type-head">
        <h3 style={{ margin: 0 }}>{c.expertName ?? 'A consultant who has left'}</h3>
        <span className="pill">{c.safeguarding ? `SAFEGUARDING · ${c.status}` : c.status}</span>
      </div>
      <p className="muted" style={{ margin: 0 }}>From {c.familyEmail ?? 'unknown'} · {new Date(c.createdAt).toLocaleString()}</p>
      <p className="ai-text" style={{ margin: 0 }}>{c.details}</p>
      {c.status === 'CLOSED'
        ? <p style={{ margin: 0 }}><strong>Outcome:</strong> {c.outcome}</p>
        : (
          <div className="assist-form">
            <div className="field">
              <label htmlFor={`outcome-${c.id}`}>Outcome</label>
              <textarea id={`outcome-${c.id}`} rows={3} maxLength={2000} value={outcome} onChange={(e) => setOutcome(e.target.value)} />
            </div>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="toolbar"><button type="button" className="btn" disabled={busy || !outcome.trim()} onClick={() => void close()}>Close concern</button></div>
          </div>
        )}
    </article>
  );
}

export default function AssistAdmin() {
  const [experts, setExperts] = useState<AdminExpert[] | null>(null);
  const [concerns, setConcerns] = useState<Concern[]>([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api<AdminExpert[]>('/assist/admin/experts', { token: true }).then(setExperts).catch((e) => setError(failed(e)));
    void api<Concern[]>('/assist/admin/concerns', { token: true }).then(setConcerns).catch(() => setConcerns([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    void api<Payout[]>(`/assist/admin/payouts?month=${month}`, { token: true }).then(setPayouts).catch(() => setPayouts([]));
  }, [month]);

  if (error) return <div className="card"><p className="error">{error}</p></div>;
  if (!experts) return null;

  const open = concerns.filter((c) => c.status === 'OPEN').length;

  return (
    <div className="assist-home">
      <div className="card">
        <p className="field-hint" style={{ marginTop: 0 }}><Link to="/assist">Assist</Link></p>
        <h1 style={{ marginTop: 0 }}>Consultants</h1>
        <p className="field-hint">Before approving, check the registration number on the professional register, open their documents, and record the DBS Update Service check.</p>
        <div className="list">
          {experts.length === 0 && <p className="muted">No applications yet.</p>}
          {experts.map((x) => <ExpertCard key={x.id} x={x} onChange={load} />)}
        </div>
      </div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Concerns{open > 0 ? ` (${open} open)` : ''}</h2>
        {concerns.length === 0 ? <p className="muted">No concerns reported.</p> : (
          <div className="list">{concerns.map((c) => <ConcernItem key={c.id} c={c} onChange={load} />)}</div>
        )}
      </div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Payouts</h2>
        <div className="field"><label htmlFor="po-month">Month</label><input id="po-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
        {payouts.length === 0 ? <p className="muted">Nothing in this month.</p> : (
          <table className="assist-table">
            <thead><tr><th>Consultant</th><th>Time logged</th><th>Owed to consultant</th><th>Paid in by families</th></tr></thead>
            <tbody>{payouts.map((p) => <tr key={p.expertId}><td>{p.name}<br /><span className="muted">{p.email}</span></td><td>{hours(p.minutes)}</td><td>{money(p.owedPence)}</td><td>{money(p.paidInPence)}</td></tr>)}</tbody>
          </table>
        )}
        <p className="field-hint">Owed is the consultant's own rate for the time they logged this month. Paid in is what families paid this month, including EasyLearning's fee; it can be for time logged in later months.</p>
      </div>
    </div>
  );
}
